'use client';

import type { JSX } from 'react';

import React, { createContext, useState, useCallback, useMemo } from 'react';
import IconComponent from '../Icon';
import Modal from '.';

export interface ModalContextProps {
  openModal: (
    content: React.ReactNode,
    customClasses?: string,
    closeIcon?: JSX.Element,
    customButtonClasses?: string,
    customCallback?: () => void,
    customCloseCallback?: () => void,
  ) => void;
  closeModal: () => void;
}

export const ModalContext = createContext<ModalContextProps>({
  openModal: () => {},
  closeModal: () => {},
});

interface ModalProviderProps {
  children: React.ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const defaultCloseIcon = useCallback(
    () => <IconComponent iconName="Close" size="IconM" className="bg-white" />,
    [],
  );

  const [modalContent, setModalContent] = useState<React.ReactNode | null>(null);
  const [modalClasses, setModalClasses] = useState('');
  const [modalButtonClasses, setModalButtonClasses] = useState('');
  const [modalCloseIcon, setModalCloseIcon] = useState(defaultCloseIcon);
  const [modalCloseCallback, setModalCloseCallback] = useState<(() => void) | null>(null);

  const openModal = useCallback(
    (
      content: React.ReactNode,
      customClasses?: string,
      closeIcon?: JSX.Element,
      customButtonClasses?: string,
      customCallback?: () => void,
      customCloseCallback?: () => void,
    ) => {
      setModalContent(content);
      setModalClasses(customClasses || '');
      setModalButtonClasses(customButtonClasses || '');
      setModalCloseIcon(closeIcon || defaultCloseIcon);
      setModalCloseCallback(customCloseCallback ? () => customCloseCallback : null);

      if (customCallback) {
        customCallback();
      }
    },
    [defaultCloseIcon],
  );

  const closeModal = useCallback(() => {
    if (modalCloseCallback) {
      modalCloseCallback();
    }
    setModalContent(null);
    setModalClasses('');
    setModalCloseIcon(defaultCloseIcon);
    setModalCloseCallback(null);
  }, [defaultCloseIcon, modalCloseCallback]);

  const modalContextValue = useMemo(
    () => ({
      openModal,
      closeModal,
    }),
    [openModal, closeModal],
  );

  return (
    <ModalContext.Provider value={modalContextValue}>
      {children}
      {modalContent && (
        <Modal
          classes={modalClasses}
          onClose={closeModal}
          closeIcon={modalCloseIcon}
          buttonClasses={modalButtonClasses}
        >
          {modalContent}
        </Modal>
      )}
    </ModalContext.Provider>
  );
}
